'use client';

import { useEffect, useEffectEvent } from 'react';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { useSupabase } from './use-supabase';

/**
 * @name PRIVATE_PATH_PREFIXES
 * @description A list of private path prefixes
 */
const PRIVATE_PATH_PREFIXES = [
  '/home',
  '/admin',
  '/join',
  '/identities',
  '/update-password',
];

/**
 * @name AUTH_PATHS
 * @description A list of auth paths
 */
const AUTH_PATHS = ['/auth'];

/**
 * Pure decision: should the listener reload the window in response to a
 * SIGNED_OUT event? Exported so tests can exercise the real logic.
 */
export function shouldReloadOnSignedOut(opts: {
  pathName: string;
  isPrivateRoute: (path: string) => boolean;
  isOperationInProgress?: () => boolean;
}): boolean {
  if (opts.isOperationInProgress?.()) {
    return false;
  }

  if (AUTH_PATHS.some((path) => opts.pathName.startsWith(path))) {
    return false;
  }

  if (opts.isPrivateRoute(opts.pathName)) {
    return false;
  }

  return true;
}

/**
 * @name useAuthChangeListener
 * @param privatePathPrefixes - Fallback prefix list; used when isPrivateFn is
 *   not provided. Middleware may rewrite arbitrary paths on tenant subdomains,
 *   so a prefix list can be structurally incomplete — prefer isPrivateFn.
 * @param isPrivateFn - Optional predicate that decides whether a path is a
 *   private (authenticated) route. When provided, overrides the prefix check.
 *   Lets the app layer key on hostname (e.g. team subdomain) rather than a
 *   hand-maintained path allowlist that drifts as new routes ship.
 * @param onEvent - Callback function to be called when an auth event occurs
 * @param isOperationInProgress - Optional callback; if it returns true, auth
 *   redirects and reloads are suppressed (e.g. while a recording is active).
 *   NOTE: this also suppresses the sign-out-on-invalid-session redirect. A
 *   revoked session mid-operation surfaces later, at save time (e.g. the
 *   Supabase write returning 401), which is the intended catch point.
 */
export function useAuthChangeListener({
  privatePathPrefixes = PRIVATE_PATH_PREFIXES,
  isPrivateFn,
  onEvent,
  isOperationInProgress,
}: {
  privatePathPrefixes?: string[];
  isPrivateFn?: (path: string) => boolean;
  onEvent?: (event: AuthChangeEvent, user: Session | null) => void;
  isOperationInProgress?: () => boolean;
}) {
  const client = useSupabase();

  const setupAuthListener = useEffectEvent(() => {
    // don't run on the server
    if (typeof window === 'undefined') {
      return;
    }

    let isSigningOut = false;

    const isPathPrivate =
      isPrivateFn ??
      ((path: string) => isPrivateRoute(path, privatePathPrefixes));

    // keep this running for the whole session unless the component was unmounted
    return client.auth.onAuthStateChange((event, user) => {
      const pathName = window.location.pathname;

      if (onEvent) {
        onEvent(event, user);
      }

      // Suppress auth-driven navigation while a long-running operation (e.g.
      // an active recording) is in progress to avoid aborting it mid-stream.
      // A revoked session is caught later at save-time (see hook docstring).
      if (isOperationInProgress?.()) {
        return;
      }

      // log user out if user is falsy
      // and if the current path is a private route
      const shouldRedirectUser = !user && isPathPrivate(pathName);

      if (shouldRedirectUser) {
        // Guard against infinite loop: signOut({scope:'local'}) emits
        // another SIGNED_OUT event which would re-enter this block.
        if (isSigningOut) {
          return;
        }

        isSigningOut = true;

        // Clear any stale auth cookies before redirecting
        // This prevents loops where server sees stale JWT but client detects invalid session
        void client.auth.signOut({ scope: 'local' }).finally(() => {
          // Redirect to sign-in page instead of '/' to avoid loops on team subdomains
          // On team subdomains, '/' rewrites to '/home/{slug}' which is a private route,
          // potentially causing an infinite redirect loop
          window.location.assign('/auth/sign-in');
        });

        return;
      }

      if (
        event === 'SIGNED_OUT' &&
        shouldReloadOnSignedOut({
          pathName,
          isPrivateRoute: isPathPrivate,
          isOperationInProgress,
        })
      ) {
        window.location.reload();
      }
    });
  });

  useEffect(() => {
    const listener = setupAuthListener();

    // destroy listener on un-mounts
    return () => {
      listener?.data.subscription.unsubscribe();
    };
  }, []);
}

/**
 * Determines if a given path is a private route.
 */
function isPrivateRoute(path: string, privatePathPrefixes: string[]) {
  return privatePathPrefixes.some((prefix) => path.startsWith(prefix));
}
